import isHotkey from 'is-hotkey';
import React from 'react';
import { SearchBarFilterType } from '@fabric-app/business/search/searchbar.types';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { SearchBarButton } from './SearchbarButton';
import { searchBarFilterConfigs, searchBarMotionLayoutIdProps } from '../searchbar.config';
import { IconNavigational24Filter } from '@fabric-app/icons/svg';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';

type AddFilterDropdownProps = React.ComponentPropsWithoutRef<'div'> & {
  onFilterSelect?: (filterType: SearchBarFilterType) => void;
};

const AddFilterDropdown: React.FC<AddFilterDropdownProps> = ({ onFilterSelect }) => {
  const [open, setOpen] = React.useState(false);

  const onSelectFilter = (
    e: React.SyntheticEvent<HTMLButtonElement, Event>,
    filterType: SearchBarFilterType,
  ) => {
    e.preventDefault();
    setOpen(false);

    // We wait for the dropdown to close before calling the onSelect callback
    // This was to make sure we can focus the filter pill after it's creation, otherwise
    // we can't focus it due to the dropdown making the body have no pointer events, which
    // makes it impossible to focus it's children.
    setTimeout(() => {
      onFilterSelect?.(filterType);
    }, 200);
  };

  const onEscapeKeyDown = (e: KeyboardEvent) => {
    if (!isHotkey('esc', e)) return;

    e.stopPropagation();
    setOpen(false);
  };

  const { isMobileView } = useResponsive();

  return (
    <DropdownMenu.Base
      renderTriggerElement={({ onOpenChange }) => {
        return (
          <DropdownMenu.Trigger onOpenChange={onOpenChange} asChild>
            <SearchBarButton {...searchBarMotionLayoutIdProps.addFilterButton}>
              <IconNavigational24Filter style={{ width: 24, height: 24 }} />
            </SearchBarButton>
          </DropdownMenu.Trigger>
        );
      }}
      openProps={{
        open,
        onOpenChange: setOpen,
      }}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      <DropdownMenu.Group>
        <DropdownMenu.Label
          style={{
            /**
             * @TODO: check up if this padding should be done in another way.
             * This is forcefully done like this due to recent changes in the dropdown and drawer components that affected multiple components
             * that dependend on them, to ensure each iteration looks correct some specific padding was added, like this one.
             */
            padding: `4px 10px ${isMobileView ? 0 : 4}px 10px`,
          }}
        >
          Add filter
        </DropdownMenu.Label>
      </DropdownMenu.Group>
      <DropdownMenu.Group>
        {Object.values(SearchBarFilterType).map((filterType) => {
          const { name, Icon } = searchBarFilterConfigs[filterType];
          return (
            <DropdownMenu.Item
              key={filterType}
              onSelect={(event) => {
                onSelectFilter(event, filterType);
              }}
            >
              <DropdownMenu.IconContainer>
                <Icon />
              </DropdownMenu.IconContainer>
              {name}
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Group>
    </DropdownMenu.Base>
  );
};

export default AddFilterDropdown;
